import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Loader2, AlertCircle, Globe, Instagram, Facebook, Tag, X } from 'lucide-react';
import { VendorCreate, VendorUpdate } from '../types';
import { useVendor } from '../contexts/VendorContext';
import VendorImageUploader from './VendorImageUploader';

/**
 * Vendor Profile Form Props
 */
interface VendorProfileFormProps {
  isEdit?: boolean;
}

/**
 * Vendor Profile Form Component
 *
 * @component
 * @description Form for creating and editing vendor profiles. Supports business information,
 * product types, social media links, and portfolio image management.
 *
 * ## E2E Testing
 *
 * ### Required Fields (form won't submit without these):
 * - `business_name` (`#business_name`) - Business or artist name, min 2 chars, max 255 chars
 *
 * ### Optional Fields:
 * - `description` (`#description`) - Brief business description, max 1000 chars
 * - `bio` (`#bio`) - Artist biography, max 2000 chars
 * - `website` (`#website`) - Website URL (must be valid http/https URL)
 * - `social_media_instagram` (`#social_media_instagram`) - Instagram profile URL
 * - `social_media_facebook` (`#social_media_facebook`) - Facebook page URL
 *
 * ### Product Types Management:
 * - Input: `[data-testid="product-type-input"]`
 * - Add Button: `[data-testid="add-product-type-button"]`
 * - Press Enter in input to add product type
 * - Click X on tag to remove product type
 *
 * ### Validation Rules:
 * - Business name: Required, min 2 chars, max 255 chars
 * - Description: Max 1000 chars
 * - Bio: Max 2000 chars
 * - URLs: Must be valid http/https format if provided
 * - Product types: No duplicates allowed
 *
 * ### Portfolio Images:
 * - Only available in edit mode (after initial profile creation)
 * - Max 10 images
 * - Managed through VendorImageUploader component
 *
 * ### Routes:
 * - Create: `/vendor/profile/create`
 * - Edit: `/vendor/profile/:vendorId/edit`
 *
 * @example
 * // E2E Test Example - Create Vendor Profile
 * await page.fill('#business_name', 'Artisan Pottery Co');
 * await page.fill('#description', 'Handcrafted pottery and ceramics');
 * await page.fill('#bio', 'Creating beautiful pottery for over 10 years...');
 * await page.fill('#website', 'https://artisanpottery.com');
 * await page.fill('[data-testid="product-type-input"]', 'Pottery');
 * await page.click('[data-testid="add-product-type-button"]');
 * await page.click('[data-testid="save-vendor-profile-button"]');
 * await page.waitForURL(/\/vendor\/dashboard/);
 *
 * @see {@link ../../../tests/e2e/docs/FORM_FIELDS_REFERENCE.md#vendorprofileform} - Complete field documentation
 * @see {@link Issue #990} - Form validation rules documentation
 */

// Common product types for suggestions
const SUGGESTED_PRODUCT_TYPES = [
  'Jewelry', 'Pottery', 'Paintings', 'Photography', 'Woodwork',
  'Textiles', 'Candles', 'Soap', 'Leather Goods', 'Glass Art',
  'Metalwork', 'Prints', 'Sculptures', 'Clothing', 'Accessories',
  'Home Decor', 'Paper Goods', 'Food', 'Crafts', 'Mixed Media'
];

export default function VendorProfileForm({ isEdit = false }: VendorProfileFormProps) {
  const navigate = useNavigate();
  const { vendorId } = useParams<{ vendorId: string }>();
  const {
    currentVendor,
    loadVendor,
    createVendor,
    updateVendor,
    uploadPortfolioImage,
    removePortfolioImage,
    isLoading,
    error,
    clearError,
  } = useVendor();

  const [formData, setFormData] = useState<VendorCreate & { product_types: string[] }>({
    business_name: '',
    description: '',
    bio: '',
    website: '',
    social_media_facebook: '',
    social_media_instagram: '',
    product_types: [],
  });

  const [newProductType, setNewProductType] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [portfolioImages, setPortfolioImages] = useState<string[]>([]);

  // Load vendor data if editing
  useEffect(() => {
    if (isEdit && vendorId) {
      const id = parseInt(vendorId, 10);
      if (!isNaN(id)) {
        loadVendor(id);
      }
    }
  }, [isEdit, vendorId, loadVendor]);

  // Populate form when vendor data loads
  useEffect(() => {
    if (isEdit && currentVendor) {
      setFormData({
        business_name: currentVendor.business_name || '',
        description: currentVendor.description || '',
        bio: currentVendor.bio || '',
        website: currentVendor.website || '',
        social_media_facebook: currentVendor.social_media_facebook || '',
        social_media_instagram: currentVendor.social_media_instagram || '',
        product_types: currentVendor.product_types || [],
      });
      setPortfolioImages(currentVendor.portfolio_images || []);
    }
  }, [isEdit, currentVendor]);

  const validateUrl = (url: string): boolean => {
    if (!url) return true; // Empty is valid (optional field)
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.business_name.trim()) {
      errors.business_name = 'Business name is required';
    } else if (formData.business_name.trim().length < 2) {
      errors.business_name = 'Business name must be at least 2 characters';
    } else if (formData.business_name.trim().length > 255) {
      errors.business_name = 'Business name must be less than 255 characters';
    }

    if (formData.description && formData.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }

    if (formData.bio && formData.bio.length > 2000) {
      errors.bio = 'Bio must be less than 2000 characters';
    }

    if (formData.website && !validateUrl(formData.website)) {
      errors.website = 'Please enter a valid URL (http:// or https://)';
    }

    if (formData.social_media_facebook && !validateUrl(formData.social_media_facebook)) {
      errors.social_media_facebook = 'Please enter a valid Facebook URL';
    }

    if (formData.social_media_instagram && !validateUrl(formData.social_media_instagram)) {
      errors.social_media_instagram = 'Please enter a valid Instagram URL';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddProductType = (type: string) => {
    const trimmed = type.trim();
    if (trimmed && !formData.product_types.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        product_types: [...prev.product_types, trimmed]
      }));
    }
    setNewProductType('');
  };

  const handleRemoveProductType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      product_types: prev.product_types.filter(t => t !== type)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validate()) return;

    setIsSaving(true);
    try {
      if (isEdit && vendorId) {
        const updateData: VendorUpdate = {
          business_name: formData.business_name,
          description: formData.description || undefined,
          bio: formData.bio || undefined,
          website: formData.website || undefined,
          social_media_facebook: formData.social_media_facebook || undefined,
          social_media_instagram: formData.social_media_instagram || undefined,
          product_types: formData.product_types.length > 0 ? formData.product_types : undefined,
        };
        await updateVendor(parseInt(vendorId, 10), updateData);
        navigate('/vendor/dashboard');
      } else {
        const vendor = await createVendor(formData);
        navigate(`/vendor/profile/${vendor.id}/edit`);
      }
    } catch {
      // Error is handled by context
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    if (!isEdit || !vendorId) {
      throw new Error('Please save your profile first before uploading images');
    }
    const url = await uploadPortfolioImage(parseInt(vendorId, 10), file);
    setPortfolioImages(prev => [...prev, url]);
    return url;
  };

  const handleImageRemove = async (imageUrl: string): Promise<void> => {
    if (!isEdit || !vendorId) return;
    await removePortfolioImage(parseInt(vendorId, 10), imageUrl);
    setPortfolioImages(prev => prev.filter(url => url !== imageUrl));
  };

  if (isEdit && isLoading && !currentVendor) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          data-testid="vendor-profile-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="vendor-profile-form-heading">
          {isEdit ? 'Edit Vendor Profile' : 'Create Vendor Profile'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEdit
            ? 'Update your business information and portfolio'
            : 'Set up your vendor profile to start booking fair slots'}
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="vendor-profile-error-alert">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button onClick={clearError} className="text-red-500 hover:text-red-700" data-testid="vendor-profile-close-error-button">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Business Information */}
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Business Information</h2>

          <div className="space-y-6">
            {/* Business Name */}
            <div>
              <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-1">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                maxLength={255}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                  validationErrors.business_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Your business or artist name"
                data-testid="vendor-business-name-input"
              />
              {validationErrors.business_name && (
                <p className="mt-1 text-sm text-red-600" data-testid="business-name-error">{validationErrors.business_name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                maxLength={1000}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                  validationErrors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Brief description of your business"
                data-testid="vendor-description-input"
              />
              <div className="flex justify-between mt-1">
                {validationErrors.description ? (
                  <p className="text-sm text-red-600">{validationErrors.description}</p>
                ) : <span />}
                <span className="text-xs text-gray-400">{formData.description?.length || 0}/1000</span>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                Artist Bio
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
                maxLength={2000}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                  validationErrors.bio ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Tell visitors about yourself and your art..."
                data-testid="vendor-bio-input"
              />
              <div className="flex justify-between mt-1">
                {validationErrors.bio ? (
                  <p className="text-sm text-red-600">{validationErrors.bio}</p>
                ) : <span />}
                <span className="text-xs text-gray-400">{formData.bio?.length || 0}/2000</span>
              </div>
            </div>
          </div>
        </section>

        {/* Product Types */}
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Product Types</h2>
          <p className="text-gray-600 text-sm mb-4">
            Add the types of products you sell to help fair organizers and customers find you.
          </p>

          {/* Selected Product Types */}
          {formData.product_types.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.product_types.map(type => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-primary-dark rounded-full text-sm"
                >
                  <Tag className="w-3 h-3" />
                  {type}
                  <button
                    type="button"
                    onClick={() => handleRemoveProductType(type)}
                    className="ml-1 hover:text-teal-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Add Custom Product Type */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newProductType}
              onChange={e => setNewProductType(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddProductType(newProductType);
                }
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Add a product type..."
              data-testid="product-type-input"
            />
            <button
              type="button"
              onClick={() => handleAddProductType(newProductType)}
              disabled={!newProductType.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-product-type-button"
            >
              Add
            </button>
          </div>

          {/* Suggested Product Types */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Suggestions:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_PRODUCT_TYPES
                .filter(type => !formData.product_types.includes(type))
                .slice(0, 10)
                .map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleAddProductType(type)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    + {type}
                  </button>
                ))}
            </div>
          </div>
        </section>

        {/* Online Presence */}
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Online Presence</h2>

          <div className="space-y-4">
            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                    validationErrors.website ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://yourwebsite.com"
                  data-testid="vendor-website-input"
                />
              </div>
              {validationErrors.website && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.website}</p>
              )}
            </div>

            {/* Instagram */}
            <div>
              <label htmlFor="social_media_instagram" className="block text-sm font-medium text-gray-700 mb-1">
                Instagram
              </label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  id="social_media_instagram"
                  name="social_media_instagram"
                  value={formData.social_media_instagram}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                    validationErrors.social_media_instagram ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://instagram.com/yourhandle"
                  data-testid="vendor-instagram-input"
                />
              </div>
              {validationErrors.social_media_instagram && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.social_media_instagram}</p>
              )}
            </div>

            {/* Facebook */}
            <div>
              <label htmlFor="social_media_facebook" className="block text-sm font-medium text-gray-700 mb-1">
                Facebook
              </label>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  id="social_media_facebook"
                  name="social_media_facebook"
                  value={formData.social_media_facebook}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                    validationErrors.social_media_facebook ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://facebook.com/yourpage"
                  data-testid="vendor-facebook-input"
                />
              </div>
              {validationErrors.social_media_facebook && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.social_media_facebook}</p>
              )}
            </div>
          </div>
        </section>

        {/* Portfolio Images - Only show when editing */}
        {isEdit && vendorId && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Portfolio Images</h2>
            <p className="text-gray-600 text-sm mb-6">
              Upload images of your work to showcase your products to fair organizers and visitors.
            </p>
            <VendorImageUploader
              images={portfolioImages}
              maxImages={10}
              onUpload={handleImageUpload}
              onRemove={handleImageRemove}
              disabled={isSaving}
            />
          </section>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            data-testid="cancel-vendor-profile-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-vendor-profile-button"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEdit ? 'Save Changes' : 'Create Profile'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
